"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { buildTheses, type ThesisType } from "@/lib/tool-kit/essay/thesis";

/* ------------------------------------------------------------------ */
/*  Thesis types and generation logic                                 */
/* ------------------------------------------------------------------ */

type Strings = {
  [K in keyof typeof THESIS_STATEMENT_GENERATOR_STRINGS]: string;
};

const THESIS_TYPES: {
  value: ThesisType;
  label: keyof Strings;
  description: keyof Strings;
}[] = [
  {
    value: "argumentative",
    label: "typeArgumentativeLabel",
    description: "typeArgumentativeDescription",
  },
  {
    value: "analytical",
    label: "typeAnalyticalLabel",
    description: "typeAnalyticalDescription",
  },
  {
    value: "expository",
    label: "typeExpositoryLabel",
    description: "typeExpositoryDescription",
  },
];

interface ThesisResult {
  type: ThesisType;
  label: string;
  statement: string;
}

/* ------------------------------------------------------------------ */
/*  English strings. A translated content file overrides these        */
/*  through `ui`.                                                     */
/* ------------------------------------------------------------------ */

export const THESIS_STATEMENT_GENERATOR_STRINGS = {
  topic: "Topic",
  topicPlaceholder: "e.g., social media's impact on teenage mental health",
  positionLabel: "Your Position / Claim",
  positionPlaceholder:
    "e.g., social media has a net negative effect on teen mental health",
  reason1: "Supporting Reason 1",
  reason1Placeholder: "e.g., rising anxiety and depression rates",
  reason2: "Supporting Reason 2",
  reason2Placeholder: "e.g., unrealistic body image standards",
  reason3: "Supporting Reason 3",
  reason3Placeholder:
    "e.g., disrupted sleep patterns that are essential for development",
  frameArgumentative:
    "Although some may disagree, {claim} because of {reasons}.",
  frameAnalytical:
    "An analysis of {topic} reveals {reasons}, which together demonstrate that {claim}.",
  frameExpository: "{topic} can be understood through {reasons}.",
  placeholderTopic: "[your topic]",
  placeholderClaim: "[your position]",
  placeholderReason: "[reason {number}]",
  generateThesisStatements: "Generate Thesis Statements",
  generatedThesisStatements: "Generated Thesis Statements",
  copied: "Copied!",
  copy: "Copy",
  typeArgumentativeLabel: "Argumentative",
  typeArgumentativeDescription:
    "Takes a clear side on a debatable issue and argues for it.",
  typeAnalyticalLabel: "Analytical",
  typeAnalyticalDescription:
    "Breaks down a topic into parts and examines each component.",
  typeExpositoryLabel: "Expository",
  typeExpositoryDescription:
    "Explains a topic or process to the reader without taking a position.",
} as const;

/* ------------------------------------------------------------------ */
/*  Widget Component                                                  */
/* ------------------------------------------------------------------ */

export default function ThesisStatementGeneratorWidget() {
  const strings = useToolUi(THESIS_STATEMENT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const [topic, setTopic] = useState("");
  const [position, setPosition] = useState("");
  const [reason1, setReason1] = useState("");
  const [reason2, setReason2] = useState("");
  const [reason3, setReason3] = useState("");
  const [results, setResults] = useState<ThesisResult[] | null>(null);
  const [copied, setCopied] = useState<number | null>(null);

  function handleGenerate() {
    const theses = buildTheses(
      { topic, claim: position, reasons: [reason1, reason2, reason3] },
      {
        argumentative: strings.frameArgumentative,
        analytical: strings.frameAnalytical,
        expository: strings.frameExpository,
      },
      {
        topic: strings.placeholderTopic,
        claim: strings.placeholderClaim,
        reason: strings.placeholderReason,
      },
      locale
    );
    setResults(
      THESIS_TYPES.map((type) => ({
        type: type.value,
        label: strings[type.label],
        statement: theses[type.value],
      }))
    );
    setCopied(null);
  }

  async function handleCopy(index: number, text: string) {
    if (!(await writeToClipboard(text))) {
      return;
    }
    setCopied(index);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <>
      {/* Thesis Types Overview */}
      <div className="grid gap-3 sm:grid-cols-3">
        {THESIS_TYPES.map((t) => (
          <div
            className="rounded-lg border border-border bg-card p-4"
            key={t.value}
          >
            <p className="font-medium text-foreground text-sm">
              {strings[t.label]}
            </p>
            <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
              {strings[t.description]}
            </p>
          </div>
        ))}
      </div>

      {/* Form */}
      <div className="space-y-5 rounded-lg border border-border bg-card p-6">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="tsg-topic"
          >
            {strings.topic}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-background p-2.5 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="tsg-topic"
            onChange={(e) => setTopic(e.target.value)}
            placeholder={strings.topicPlaceholder}
            type="text"
            value={topic}
          />
        </div>

        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="tsg-position"
          >
            {strings.positionLabel}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-background p-2.5 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="tsg-position"
            onChange={(e) => setPosition(e.target.value)}
            placeholder={strings.positionPlaceholder}
            type="text"
            value={position}
          />
        </div>

        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="tsg-r1"
          >
            {strings.reason1}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-background p-2.5 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="tsg-r1"
            onChange={(e) => setReason1(e.target.value)}
            placeholder={strings.reason1Placeholder}
            type="text"
            value={reason1}
          />
        </div>

        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="tsg-r2"
          >
            {strings.reason2}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-background p-2.5 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="tsg-r2"
            onChange={(e) => setReason2(e.target.value)}
            placeholder={strings.reason2Placeholder}
            type="text"
            value={reason2}
          />
        </div>

        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="tsg-r3"
          >
            {strings.reason3}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-background p-2.5 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="tsg-r3"
            onChange={(e) => setReason3(e.target.value)}
            placeholder={strings.reason3Placeholder}
            type="text"
            value={reason3}
          />
        </div>

        <button
          className="rounded-lg bg-primary px-6 py-2.5 font-medium text-primary-foreground text-sm transition-colors hover:bg-primary/90"
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateThesisStatements}
        </button>
      </div>

      {/* Results */}
      {results && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.generatedThesisStatements}
          </h2>
          <div className="space-y-4">
            {results.map((r, i) => (
              <div
                className="rounded-lg border border-border bg-muted/30 p-5"
                key={r.type}
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-md bg-primary/10 px-2 py-0.5 font-medium text-primary text-xs">
                    {r.label}
                  </span>
                  <button
                    className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
                    onClick={() => handleCopy(i, r.statement)}
                    type="button"
                  >
                    {copied === i ? strings.copied : strings.copy}
                  </button>
                </div>
                <p className="mt-3 text-foreground text-sm leading-relaxed">
                  {r.statement}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
